import { Button, Icon } from '@design-system';
import { useTranslation } from 'next-i18next';

import { useDialogContext } from '../../../hooks/useDialogContext';
import { Image } from '../../Image';

export const useFramingGuides = () => {
  const { t } = useTranslation('product');
  const { hideDialog, showDialog } = useDialogContext();

  const handleShowGlazingGuide = () => {
    const sectionOneList = t(
      'drawers.review.framing.upgrade.guide.section1.list',
      { returnObjects: true },
    );
    const sectionTwoList = t(
      'drawers.review.framing.upgrade.guide.section2.list',
      { returnObjects: true },
    );
    const sectionOneListKeys = Array.isArray(sectionOneList)
      ? (sectionOneList as unknown[]).filter(
          (item): item is string => typeof item === 'string',
        )
      : [];
    const sectionTwoListKeys = Array.isArray(sectionTwoList)
      ? (sectionTwoList as unknown[]).filter(
          (item): item is string => typeof item === 'string',
        )
      : [];

    return showDialog(
      <div className="relative flex max-h-[80dvh] w-full flex-col overflow-y-scroll rounded-t-lg bg-white no-scrollbar md:max-w-md md:overflow-y-auto md:rounded-lg">
        <div className="sticky top-0 z-30 flex w-full items-center justify-center bg-white p-6">
          <div className="text-center text-md font-bold">
            {t('drawers.review.framing.upgrade.guide.title')}
          </div>
          <div className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center">
            <Button
              hideLabel
              icon="ic_close"
              label="Close"
              onClick={hideDialog}
              size="sm"
              tabIndex={0}
              type="button"
              variant="transparent"
            />
          </div>
        </div>
        <div className="flex flex-col gap-6 p-6 pt-0">
          <div className="flex flex-col gap-3">
            <p className="text-pretty font-bold">
              {t('drawers.review.framing.upgrade.guide.section1.title')}
            </p>
            <ul>
              {sectionOneListKeys.map((v) => (
                <li key={v} className="flex gap-1 text-pretty">
                  <Icon name="ic_check" className="size-5 shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
            <Image
              alt={`Image for: ${t(
                'drawers.review.framing.upgrade.guide.section1.title',
              )}`}
              src="https://images.avantarte.org/media/3BiS6DG5hzo2atKFlt6YAubT7Qx"
              size={{
                sm: { ratio: '4/3', span: 12 },
                md: { ratio: '4/3', span: 4 },
                lg: { ratio: '4/3', span: 4 },
              }}
            />
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-pretty font-bold">
              {t('drawers.review.framing.upgrade.guide.section2.title')}
            </p>
            <ul>
              {sectionTwoListKeys.map((v) => (
                <li key={v} className="flex gap-1 text-pretty">
                  <Icon name="ic_check" className="size-5 shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
            <Image
              alt={`Image for: ${t(
                'drawers.review.framing.upgrade.guide.section2.title',
              )}`}
              src="https://images.avantarte.org/media/3BiS6DA0In5j3RRUGP2JIKW9fjh"
              size={{
                sm: { ratio: '4/3', span: 12 },
                md: { ratio: '4/3', span: 4 },
                lg: { ratio: '4/3', span: 4 },
              }}
            />
          </div>
        </div>
      </div>,
      { overlayOtherDialogs: true },
    );
  };

  const handleShowMountingGuide = () =>
    showDialog(
      <div className="relative flex max-h-[80dvh] w-full flex-col overflow-y-scroll rounded-t-lg bg-white no-scrollbar md:max-w-md md:overflow-y-auto md:rounded-lg">
        <div className="sticky top-0 z-30 flex w-full items-center justify-center bg-white p-6">
          <div className="text-center text-md font-bold">
            {t('drawers.review.framing.upgrade.mounting.guide.title')}
          </div>
          <div className="absolute right-5 flex h-9 w-9 items-center justify-center">
            <Button
              hideLabel
              icon="ic_close"
              label="Close"
              onClick={hideDialog}
              size="sm"
              tabIndex={0}
              type="button"
              variant="transparent"
            />
          </div>
        </div>
        <div className="flex flex-col gap-6 p-6 pt-0">
          <div className="flex flex-col gap-3">
            <p className="text-pretty font-bold">
              {t(
                'drawers.review.framing.upgrade.mounting.guide.section1.title',
              )}
            </p>
            <p className="text-pretty">
              {t(
                'drawers.review.framing.upgrade.mounting.guide.section1.description',
              )}
            </p>
            <Image
              alt={`Image for: ${t(
                'drawers.review.framing.upgrade.mounting.guide.section1.title',
              )}`}
              src="https://images.avantarte.org/media/3BiS60il49OhaVIP9rlSEE099H0"
              size={{
                sm: { ratio: '4/3', span: 12 },
                md: { ratio: '4/3', span: 4 },
                lg: { ratio: '4/3', span: 4 },
              }}
            />
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-pretty font-bold">
              {t(
                'drawers.review.framing.upgrade.mounting.guide.section2.title',
              )}
            </p>
            <p className="text-pretty">
              {t(
                'drawers.review.framing.upgrade.mounting.guide.section2.description',
              )}
            </p>
            <Image
              alt={`Image for: ${t(
                'drawers.review.framing.upgrade.mounting.guide.section2.title',
              )}`}
              src="https://images.avantarte.org/media/3BiS67sTOjeMgnGWfQ8ahkmnelg"
              size={{
                sm: { ratio: '4/3', span: 12 },
                md: { ratio: '4/3', span: 4 },
                lg: { ratio: '4/3', span: 4 },
              }}
            />
          </div>
        </div>
      </div>,
      { overlayOtherDialogs: true },
    );

  return { handleShowGlazingGuide, handleShowMountingGuide };
};
